import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createClient } from '@supabase/supabase-js';
import RentalBookingModal from './components/RentalBookingModal';
import VehicleDetailsModal from './components/VehicleDetailsModal';
import TradeInModal from './components/TradeInModal';
import LanguageSwitcher from '../../components/base/LanguageSwitcher';
import { updateMetaTags, addSchemaOrg, generateOrganizationSchema, generateItemListSchema } from '../../utils/seo';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function HomePage() {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [isRentalModalOpen, setIsRentalModalOpen] = useState(false);
  const [selectedRentalType, setSelectedRentalType] = useState<'daily' | 'weekly' | 'monthly'>('weekly');
  const [isVehicleDetailsOpen, setIsVehicleDetailsOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<any>(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isTradeInModalOpen, setIsTradeInModalOpen] = useState(false);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Calculate rental pricing
  const baseRates = {
    weekly: 599,
    monthly: 2599
  };

  useEffect(() => {
    fetchVehicles();
    
    // Update SEO meta tags
    updateMetaTags({
      title: 'Commercial Truck & Van Center - Quality Used Commercial Vehicles in Montreal',
      description: 'Your trusted partner for quality used commercial trucks and cargo vans in Montreal, Quebec. We offer sales, trade-ins, rentals, and financing solutions for all your business vehicle needs. Box trucks, cargo vans, reefer trucks available.',
      keywords: 'commercial trucks Montreal, cargo vans Montreal, box trucks Quebec, commercial vehicle sales, truck rental Montreal, van financing, used commercial trucks, reefer trucks, tailgate trucks, business vehicles Montreal',
      canonical: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/`
    });

    // Add Organization Schema
    addSchemaOrg(generateOrganizationSchema());
  }, []);

  useEffect(() => {
    // Add ItemList Schema when vehicles are loaded
    if (vehicles.length > 0) {
      const organizationSchema = generateOrganizationSchema();
      const itemListSchema = generateItemListSchema(vehicles.slice(0, 10)); // Top 10 vehicles
      
      // Combine schemas
      const combinedSchema = {
        '@context': 'https://schema.org',
        '@graph': [organizationSchema, itemListSchema]
      };
      
      addSchemaOrg(combinedSchema);
    }
  }, [vehicles]);

  const fetchVehicles = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('vehicles')
        .select('*')
        .eq('available_for_sale', true)
        .eq('status', 'available')
        .eq('is_visible', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Database error:', error);
        throw error;
      }
      
      console.log('Fetched visible vehicles from database:', data);
      console.log('Number of visible vehicles:', data?.length || 0);
      setVehicles(data || []);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      setVehicles([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to open AI Assistant widget
  const openAIAssistant = () => {
    const maxAttempts = 30; // Increased from 10 to 30 for slower connections
    let attempts = 0;

    const tryOpenWidget = () => {
      const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement | null;
      
      if (widget) {
        console.log('AI Assistant widget found, opening...');
        widget.click();
      } else {
        attempts++;
        if (attempts < maxAttempts) {
          console.log(`AI Assistant widget not ready, retrying... (${attempts}/${maxAttempts})`);
          setTimeout(tryOpenWidget, 500); // Increased delay to 500ms
        } else {
          console.error('AI Assistant widget failed to load after multiple attempts');
          alert('The AI Assistant is still loading. Please wait a moment and try again, or refresh the page.');
        }
      }
    };

    // Show immediate feedback to user
    const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement | null;
    if (!widget) {
      console.log('Widget not immediately available, waiting for it to load...');
    }

    tryOpenWidget();
  };

  const scrollToInventory = () => {
    const inventorySection = document.getElementById('inventory-section');
    if (inventorySection) {
      inventorySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToRentals = () => {
    const rentalsSection = document.getElementById('rentals-section');
    if (rentalsSection) {
      rentalsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const openRentalModal = (type: 'daily' | 'weekly' | 'monthly') => {
    setSelectedRentalType(type);
    setIsRentalModalOpen(true);
  };

  const openVehicleDetails = (vehicle: any) => {
    setSelectedVehicle(vehicle);
    setIsVehicleDetailsOpen(true);
  };

  // REMOVED MOCK DATA - Only use real database vehicles
  const filteredVehicles = selectedCategory === 'all' 
    ? vehicles 
    : vehicles.filter(vehicle => vehicle.category === selectedCategory);

  return (
    <div className="min-h-screen bg-white">
      {/* Language Switcher - Fixed Position */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>

      {/* Hero Section */}
      <section 
        className="relative h-screen flex items-center justify-center text-white overflow-hidden"
      >
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="YOUR_VIDEO_URL_HERE.mp4" type="video/mp4" />
          {/* Fallback image if video doesn't load */}
        </video>
        
        {/* Fallback Background Image */}
        <div 
          className="absolute inset-0 w-full h-full bg-cover bg-center"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/ad4df7d2168f000c8b63f89c59f68fb4.jpg')`
          }}
        ></div>
        
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-5xl lg:text-7xl font-bold mb-6">
            {t('heroTitle')}
          </h1>
          <p className="text-xl lg:text-2xl mb-12 text-gray-100 max-w-3xl mx-auto">
            {t('heroSubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={scrollToInventory}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('viewInventory')}
            </button>
            <button 
              onClick={scrollToRentals}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('rentalOptions')}
            </button>
            <button 
              onClick={() => navigate('/financing')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('getFinancing')}
            </button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('servicesTitle')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('servicesSubtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center group">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-blue-200 transition-colors">
                <i className="ri-truck-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-semibold mb-4">{t('vehicleSales')}</h3>
              <p className="text-gray-600">{t('vehicleSalesDesc')}</p>
            </div>
            
            <div className="text-center group">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-green-200 transition-colors">
                <i className="ri-exchange-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-semibold mb-4">{t('tradeIns')}</h3>
              <p className="text-gray-600">{t('tradeInsDesc')}</p>
            </div>
            
            <div className="text-center group">
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-purple-200 transition-colors">
                <i className="ri-calendar-line text-3xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-semibold mb-4">{t('rentalsService')}</h3>
              <p className="text-gray-600">{t('rentalsServiceDesc')}</p>
            </div>
            
            <div className="text-center group">
              <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-orange-200 transition-colors">
                <i className="ri-bank-card-line text-3xl text-orange-600"></i>
              </div>
              <h3 className="text-xl font-semibold mb-4">{t('financingService')}</h3>
              <p className="text-gray-600">{t('financingServiceDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Vehicles Section */}
      <section id="inventory-section" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('featuredVehicles')}</h2>
            <p className="text-xl text-gray-600 mb-8">{t('featuredVehiclesDesc')}</p>
            
            {/* Admin Link */}
            <div className="mb-8">
              <button
                onClick={() => navigate('/admin')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-settings-line"></i>
                {t('manageInventory')}
              </button>
            </div>
            
            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              <button 
                onClick={() => setSelectedCategory('all')}
                className={`px-6 py-3 rounded-full font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  selectedCategory === 'all' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}>
                {t('allVehicles')}
              </button>
              <button 
                onClick={() => setSelectedCategory('truck')}
                className={`px-6 py-3 rounded-full font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  selectedCategory === 'truck' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}>
                {t('boxTrucks')}
              </button>
              <button 
                onClick={() => setSelectedCategory('van')}
                className={`px-6 py-3 rounded-full font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  selectedCategory === 'van' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}>
                {t('cargoVans')}
              </button>
              <button 
                onClick={() => setSelectedCategory('reefer')}
                className={`px-6 py-3 rounded-full font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  selectedCategory === 'reefer' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}>
                {t('reeferTrucks')}
              </button>
              <button 
                onClick={() => setSelectedCategory('tailgate')}
                className={`px-6 py-3 rounded-full font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  selectedCategory === 'tailgate' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}>
                {t('tailgateTrucks')}
              </button>
              <button 
                onClick={() => setSelectedCategory('ramp')}
                className={`px-6 py-3 rounded-full font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  selectedCategory === 'ramp' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}>
                {t('rampTrucks')}
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin mb-4"></i>
              <p className="text-gray-600">{t('loadingVehicles')}</p>
            </div>
          ) : filteredVehicles.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-inbox-line text-6xl text-gray-400 mb-4"></i>
              <p className="text-xl text-gray-600 mb-2">{t('noVehiclesFound')}</p>
              <p className="text-gray-500">{t('tryDifferentCategory')}</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8" data-product-shop>
              {filteredVehicles.map((vehicle) => (
                <div key={vehicle.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="relative">
                    <div className="w-full h-80 bg-gray-100 flex items-center justify-center">
                      <img 
                        src={vehicle.image_url || vehicle.image} 
                        alt={vehicle.title}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="absolute top-4 left-4 bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {vehicle.year}
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-xl font-semibold mb-2">{vehicle.title}</h3>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-2xl font-bold text-blue-600">${vehicle.price.toLocaleString()}</span>
                      {vehicle.mileage && (
                        <span className="text-gray-600">{vehicle.mileage.toLocaleString()} km</span>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap gap-2 mb-6">
                      {(vehicle.features || []).map((feature: string, index: number) => (
                        <span key={index} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                          {feature}
                        </span>
                      ))}
                    </div>
                    
                    <div className="flex gap-3">
                      <button 
                        onClick={() => openVehicleDetails(vehicle)}
                        className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {t('viewDetails')}
                      </button>
                      <button 
                        onClick={openAIAssistant}
                        className="px-6 py-3 border-2 border-blue-600 text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-phone-line"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <div className="text-center mt-12">
            <button className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap">
              {t('viewAllInventory')}
            </button>
          </div>
        </div>
      </section>

      {/* Rental Section */}
      <section id="rentals-section" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('flexibleRentals')}</h2>
            <p className="text-xl text-gray-600">{t('flexibleRentalsDesc')}</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Daily Rentals */}
            <div className="bg-gray-50 p-8 rounded-xl shadow-lg text-center hover:shadow-xl transition-shadow flex flex-col">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-calendar-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-2xl font-semibold mb-4">{t('dailyRentals')}</h3>
              <p className="text-gray-600 mb-6">{t('dailyRentalsDesc')}</p>
              <div className="text-3xl font-bold text-blue-600 mb-4">$199/day</div>
              <ul className="text-left space-y-2 text-gray-600 mb-6">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-green-600"></i>
                  <span>{t('boxTrucks')} & {t('cargoVans').toLowerCase()}</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-green-600"></i>
                  <span>{t('comprehensiveInsurance')}</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-green-600"></i>
                  <span>{t('unlimitedKilometers')}</span>
                </li>
              </ul>
              <div className="mt-auto">
                <button 
                  onClick={() => openRentalModal('daily')}
                  className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {t('bookDailyRental')}
                </button>
              </div>
            </div>

            {/* Weekly Rentals */}
            <div className="bg-gray-50 p-8 rounded-xl shadow-lg text-center hover:shadow-xl transition-shadow flex flex-col">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-calendar-2-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-2xl font-semibold mb-4">{t('weeklyRentals')}</h3>
              <p className="text-gray-600 mb-6">{t('weeklyRentalsDesc')}</p>
              <div className="text-3xl font-bold text-blue-600 mb-4">$599/week</div>
              <ul className="text-left space-y-2 text-gray-600 mb-6">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-green-600"></i>
                  <span>{t('boxTrucks')} & {t('cargoVans').toLowerCase()}</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-green-600"></i>
                  <span>{t('comprehensiveInsurance')}</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-green-600"></i>
                  <span>{t('unlimitedKilometers')}</span>
                </li>
              </ul>
              <div className="mt-auto">
                <button 
                  onClick={() => openRentalModal('weekly')}
                  className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {t('bookWeeklyRental')}
                </button>
              </div>
            </div>
            
            {/* Monthly Rentals */}
            <div className="bg-gray-50 p-8 rounded-xl shadow-lg text-center hover:shadow-xl transition-shadow border-2 border-blue-600 relative flex flex-col">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                {t('mostPopular')}
              </div>
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-calendar-event-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-2xl font-semibold mb-4">{t('monthlyRentals')}</h3>
              <p className="text-gray-600 mb-6">{t('monthlyRentalsDesc')}</p>
              <div className="text-3xl font-bold text-blue-600 mb-4">$2,599/month</div>
              <ul className="text-left space-y-2 text-gray-600 mb-6 flex-grow">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-green-600"></i>
                  <span>{t('fullFleetOptions')}</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-green-600"></i>
                  <span>{t('maintenanceIncluded')}</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-green-600"></i>
                  <span>{t('fleetManagementSupport')}</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-green-600"></i>
                  <span>{t('unlimitedKilometers')}</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-green-600"></i>
                  <span>{t('flexibleTerms')}</span>
                </li>
              </ul>
              <div className="mt-auto">
                <button 
                  onClick={() => openRentalModal('monthly')}
                  className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {t('bookMonthlyRental')}
                </button>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <p className="text-gray-600 mb-6">{t('customQuote')}</p>
            <button 
              onClick={openAIAssistant}
              className="bg-gray-900 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('contactCustomQuote')}
            </button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('whyChooseUs')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('whyChooseUsDesc')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-semibold mb-4">{t('qualityGuaranteed')}</h3>
              <p className="text-gray-600">{t('qualityGuaranteedDesc')}</p>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-customer-service-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-semibold mb-4">{t('expertSupport')}</h3>
              <p className="text-gray-600">{t('expertSupportDesc')}</p>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-money-dollar-circle-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-semibold mb-4">{t('competitivePricing')}</h3>
              <p className="text-gray-600">{t('competitivePricingDesc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h2 className="text-4xl font-bold text-white mb-6">{t('readyToFind')}</h2>
          <p className="text-xl text-blue-100 mb-8">{t('readyToFindDesc')}</p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
              {t('browseInventory')}
            </button>
            <button className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold transition-colors cursor-pointer whitespace-nowrap">
              {t('scheduleAppointment')}
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <img 
                src="https://static.readdy.ai/image/3a68546ea1fc6bd42c4dffe18390304b/4e0983a2a8baa9b8befdefb7167d7d65.png" 
                alt="JMA Logo" 
                className="h-16 mb-6"
              />
              <p className="text-gray-300 mb-6">{t('footerDesc')}</p>
              <div className="flex gap-4">
                <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center cursor-pointer hover:bg-gray-600 transition-colors">
                  <i className="ri-facebook-fill"></i>
                </div>
                <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center cursor-pointer hover:bg-gray-600 transition-colors">
                  <i className="ri-instagram-fill"></i>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-6">{t('services')}</h4>
              <ul className="space-y-3 text-gray-300">
                <li><a href="#vehicles" className="hover:text-white transition-colors cursor-pointer">{t('vehicleSales')}</a></li>
                <li><a href="#trade-in" className="hover:text-white transition-colors cursor-pointer">{t('tradeIns')}</a></li>
                <li><a href="#rentals" className="hover:text-white transition-colors cursor-pointer">{t('rentalsService')}</a></li>
                <li><a href="#financing" className="hover:text-white transition-colors cursor-pointer">{t('financingService')}</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-6">{t('inventory')}</h4>
              <ul className="space-y-3 text-gray-300">
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">{t('boxTrucks')}</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">{t('cargoVans')}</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">{t('reeferTrucks')}</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">{t('tailgateTrucks')}</a></li>
                <li><a href="#" className="hover:text-white transition-colors cursor-pointer">{t('rampTrucks')}</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-6">{t('contactInfo')}</h4>
              <div className="space-y-4 text-gray-300">
                <div className="flex items-center gap-3">
                  <i className="ri-phone-line"></i>
                  <span>{t('phone')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-mail-line"></i>
                  <span>{t('email')}</span>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-map-pin-line mt-1"></i>
                  <span style={{ whiteSpace: 'pre-line' }}>{t('address')}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Commercial Truck &amp; Van Center. {t('allRightsReserved')} | <a href="https://readdy.ai/?origin=logo" className="hover:text-white transition-colors cursor-pointer">{t('websiteBuilder')}</a></p>
          </div>
        </div>
      </footer>

      {/* Rental Booking Modal */}
      <RentalBookingModal
        isOpen={isRentalModalOpen}
        onClose={() => setIsRentalModalOpen(false)}
        rentalType={selectedRentalType}
      />

      {/* Vehicle Details Modal */}
      <VehicleDetailsModal
        isOpen={isVehicleDetailsOpen}
        onClose={() => setIsVehicleDetailsOpen(false)}
        vehicle={selectedVehicle}
      />

      {/* Trade-In Modal */}
      <TradeInModal
        isOpen={isTradeInModalOpen}
        onClose={() => setIsTradeInModalOpen(false)}
      />
    </div>
  );
}
